import { addMinutes, startOfDay, format, startOfWeek, endOfWeek, eachDayOfInterval } from "date-fns";
import { AvailabilityStatus } from "../types/schedule";

// Demo mode for testing without Firebase
const DEMO_MODE = true;
const demoData: any = {};

export function dateKey(d: Date): string {
  return format(d, "yyyyMMdd");
}

export function timeKey(d: Date): string {
  return format(d, "HH:mm");
}

export async function generateDaySlots(
  studioId: string, 
  day: Date, 
  startHHMM = "09:00", 
  endHHMM = "21:00", 
  slotMinutes = 90
) {
  const [sh, sm] = startHHMM.split(":").map(Number);
  const [eh, em] = endHHMM.split(":").map(Number);
  const base = new Date(day); 
  base.setHours(sh, sm, 0, 0);
  const end = new Date(day); 
  end.setHours(eh, em, 0, 0);

  const slots: { start: Date; end: Date }[] = [];
  for (let t = new Date(base); t < end; t = addMinutes(t, slotMinutes)) {
    const slotEnd = addMinutes(t, slotMinutes);
    if (slotEnd <= end) {
      slots.push({ start: new Date(t), end: slotEnd });
    }
  }

  // Demo mode - store in memory
  const dk = dateKey(day);
  if (!demoData[studioId]) demoData[studioId] = {};
  if (!demoData[studioId][dk]) demoData[studioId][dk] = {};
  
  slots.forEach((s, i) => {
    demoData[studioId][dk][String(i).padStart(3, "0")] = {
      studioId,
      dateKey: dk,
      start: s.start,
      end: s.end,
      status: "open" as AvailabilityStatus,
      source: "template" as const
    };
  });
  return slots;
}

export async function openDay(studioId: string, day: Date) {
  await generateDaySlots(studioId, day);
}

export async function blockDay(studioId: string, day: Date) {
  const dk = dateKey(day);
  
  // Demo mode - block all slots for the day
  if (!demoData[studioId]) demoData[studioId] = {};
  if (!demoData[studioId][dk]) {
    // Generate slots first if they don't exist
    await generateDaySlots(studioId, day);
  }
  
  // Block all slots
  Object.keys(demoData[studioId][dk]).forEach(slotId => {
    demoData[studioId][dk][slotId].status = "blocked";
  });
}

export async function openWeek(studioId: string, weekStart: Date) {
  const weekDays = eachDayOfInterval({
    start: startOfWeek(weekStart, { weekStartsOn: 1 }), // Maandag start
    end: endOfWeek(weekStart, { weekStartsOn: 1 })
  });

  for (const day of weekDays) {
    await openDay(studioId, day);
  }
}

export async function blockWeek(studioId: string, weekStart: Date) {
  const weekDays = eachDayOfInterval({
    start: startOfWeek(weekStart, { weekStartsOn: 1 }),
    end: endOfWeek(weekStart, { weekStartsOn: 1 })
  });

  // Demo mode - block all days and their slots
  for (const day of weekDays) {
    await blockDay(studioId, day);
  }
}

export async function blockTimeSlot(studioId: string, start: Date, end: Date, reason?: string) {
  const dk = dateKey(start);
  
  // Demo mode - block specific time slot
  if (!demoData[studioId]) demoData[studioId] = {};
  if (!demoData[studioId][dk]) {
    // Generate slots first if they don't exist
    await generateDaySlots(studioId, start);
  }
  
  // Find and block the specific time slot
  const slotId = Object.keys(demoData[studioId][dk]).find(id => {
    const slot = demoData[studioId][dk][id];
    return slot.start.getTime() === start.getTime() && slot.end.getTime() === end.getTime();
  });
  
  if (slotId) {
    demoData[studioId][dk][slotId].status = "blocked";
    if (reason) {
      demoData[studioId][dk][slotId].reason = reason;
    }
  } else {
    // Create a custom blocked slot if it doesn't match existing slots
    const customSlotId = `custom-${Date.now()}`;
    demoData[studioId][dk][customSlotId] = {
      studioId,
      dateKey: dk,
      start: start,
      end: end,
      status: "blocked" as AvailabilityStatus,
      source: "manual" as const,
      reason: reason || "Custom blocked time"
    };
  }
}

export async function unblockTimeSlot(studioId: string, start: Date, end: Date) {
  const dk = dateKey(start);
  
  // Demo mode - unblock specific time slot
  if (!demoData[studioId] || !demoData[studioId][dk]) {
    return;
  }
  
  // Find and unblock the specific time slot
  const slotId = Object.keys(demoData[studioId][dk]).find(id => {
    const slot = demoData[studioId][dk][id];
    return slot.start.getTime() === start.getTime() && slot.end.getTime() === end.getTime();
  });
  
  if (slotId) {
    demoData[studioId][dk][slotId].status = "open";
    delete demoData[studioId][dk][slotId].reason;
  }
}

export async function setWeekFull(studioId: string, weekStart: Date, isFull: boolean) {
  const weekDays = eachDayOfInterval({
    start: startOfWeek(weekStart, { weekStartsOn: 1 }),
    end: endOfWeek(weekStart, { weekStartsOn: 1 })
  });

  // Demo mode - set week full status for all days
  for (const day of weekDays) {
    const dk = dateKey(day);
    if (!demoData[studioId]) demoData[studioId] = {};
    if (!demoData[studioId][dk]) {
      // Generate slots first if they don't exist
      await generateDaySlots(studioId, day);
    }
    
    // Block all open slots if week is full
    Object.keys(demoData[studioId][dk]).forEach(slotId => {
      const slot = demoData[studioId][dk][slotId];
      if (slot.status === "open") {
        slot.status = isFull ? "blocked" : "open";
        slot.weekFull = isFull;
      }
    });
  }
}

export async function bookSlot(
  studioId: string, 
  start: Date, 
  end: Date, 
  userId: string,
  guestIds: string[] = [],
  notes?: string
) {
  const dk = dateKey(start);
  
  // Demo mode - book slot in memory
  if (!demoData[studioId] || !demoData[studioId][dk]) {
    throw new Error("Geen open slot gevonden");
  }
  
  // Find the exact slot
  const slotId = Object.keys(demoData[studioId][dk]).find(id => {
    const slot = demoData[studioId][dk][id];
    return slot.start.getTime() === start.getTime();
  });
  
  if (!slotId) {
    throw new Error("Geen open slot gevonden");
  }
  
  const slot = demoData[studioId][dk][slotId];
  if (slot.status !== "open") {
    throw new Error("Overlap met bestaand slot");
  }
  
  // Book the slot
  slot.status = "booked";
  
  // Stub notification
  await notifyBookingCreated(studioId, start, end, userId);
}

export async function moveSlot(
  studioId: string,
  oldStart: Date,
  oldEnd: Date,
  newStart: Date,
  newEnd: Date
) {
  const oldDk = dateKey(oldStart);
  const newDk = dateKey(newStart);
  
  // Demo mode - move slot in memory
  if (!demoData[studioId] || !demoData[studioId][oldDk]) {
    throw new Error("Oude slot niet gevonden");
  }
  
  // Find old slot
  const oldSlotId = Object.keys(demoData[studioId][oldDk]).find(id => {
    const slot = demoData[studioId][oldDk][id];
    return slot.start.getTime() === oldStart.getTime();
  });
  
  if (!oldSlotId) {
    throw new Error("Oude slot niet gevonden");
  }
  
  // Check if new position is available
  if (!demoData[studioId][newDk]) {
    demoData[studioId][newDk] = {};
  }
  
  const newSlotId = Object.keys(demoData[studioId][newDk]).find(id => {
    const slot = demoData[studioId][newDk][id];
    return slot.start.getTime() === newStart.getTime();
  });
  
  if (newSlotId && demoData[studioId][newDk][newSlotId].status !== "open") {
    throw new Error("Nieuwe positie is niet beschikbaar");
  }
  
  // Move the slot
  const slot = demoData[studioId][oldDk][oldSlotId];
  slot.start = newStart;
  slot.end = newEnd;
  
  if (newDk !== oldDk) {
    // Move to different day
    demoData[studioId][newDk][oldSlotId] = slot;
    delete demoData[studioId][oldDk][oldSlotId];
  }
}

export async function fetchDayAvailability(studioId: string, day: Date) {
  const dk = dateKey(day);
  
  // Demo mode - return from memory
  if (!demoData[studioId] || !demoData[studioId][dk]) {
    return [];
  }
  return Object.entries(demoData[studioId][dk]).map(([id, data]: [string, any]) => ({
    id,
    ...data
  }));
}

export async function fetchWeekAvailability(studioId: string, weekStart: Date) {
  const weekDays = eachDayOfInterval({
    start: startOfWeek(weekStart, { weekStartsOn: 1 }),
    end: endOfWeek(weekStart, { weekStartsOn: 1 })
  });

  const results = await Promise.all(
    weekDays.map(day => fetchDayAvailability(studioId, day))
  );

  return results.flat();
}

// Stub functions
export async function notifyBookingCreated(
  studioId: string,
  start: Date,
  end: Date,
  userId: string
) {
  console.log(`Booking created: ${start.toISOString()} - ${end.toISOString()} by ${userId}`);
  // Implement actual notification logic here
}

export async function exportBookingIcs(booking: any) {
  console.log(`Exporting ICS for booking:`, booking);
  // Implement ICS export logic here
}